/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Ortak AI asistan çalışma sözleşmeleri. AI ürün ekranı ve global ürün içi chat
 * aynı aksiyon, ek ve composer metin standardını buradan tüketir.
 */

export type AssistantActionType =
  | 'open_support_ticket'
  | 'capture_screenshot'
  | 'focus_support_description'
  | 'support_main_menu'
  | 'create_report_draft'
  | 'create_automation_draft'
  | 'prepare_product_form'
  | 'open_help_doc'
  | 'none';

export interface AssistantSuggestedAction {
  type: AssistantActionType;
  label: string;
  requiresConfirmation?: boolean;
  payload?: Record<string, unknown>;
}

export interface AssistantAttachmentMeta {
  id: string;
  name: string;
  type: string;
  size: number;
  dmsFileRef: string;
  supportTarget?: 'QUBEPRO_SAAS' | 'TENANT_INTERNAL';
}

export interface AssistantAttachment extends AssistantAttachmentMeta {}

export interface AssistantComposerTexts {
  menuLabel: string;
  supportTitle: string;
  supportDescription: string;
  screenTitle: string;
  screenDescription: string;
  fileTitle: string;
  fileDescription: string;
  helpTitle: string;
  helpDescription: string;
  reportTitle: string;
  reportDescription: string;
  automationTitle: string;
  automationDescription: string;
  sendTitle: string;
  stopTitle: string;
}

export type AssistantComposerActionId =
  | 'capture-screen'
  | 'attach-file'
  | 'support-ticket'
  | 'explain-screen'
  | 'report-draft'
  | 'automation-draft';

export interface AssistantComposerActionDescriptor {
  id: AssistantComposerActionId;
  title: string;
  description: string;
  iconName: string;
}

export const ASSISTANT_COMPOSER_ACTION_ORDER: AssistantComposerActionId[] = [
  'capture-screen',
  'attach-file',
  'support-ticket',
  'explain-screen',
  'report-draft',
  'automation-draft',
];

export function createAssistantId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function toAssistantAttachmentMeta(attachment: AssistantAttachment): AssistantAttachmentMeta {
  const { id, name, type, size, dmsFileRef, supportTarget } = attachment;
  return { id, name, type, size, dmsFileRef, supportTarget };
}

export function summarizeAssistantAttachments(attachments: AssistantAttachment[]): Array<Record<string, unknown>> {
  return attachments.map(({ id, name, type, size, dmsFileRef, supportTarget }) => ({
    id,
    name,
    type,
    size,
    dmsFileRef,
    supportTarget,
  }));
}

export function buildAssistantAttachmentPayload(attachments: AssistantAttachment[]): Array<Record<string, unknown>> {
  return attachments.map(({ id, name, type, size, dmsFileRef, supportTarget }) => ({
    id,
    name,
    type,
    size,
    dmsFileRef,
    supportTarget,
  }));
}

export function normalizeAssistantAttachmentMeta(value: unknown): AssistantAttachmentMeta | null {
  if (!value || typeof value !== 'object') return null;
  const item = value as Record<string, unknown>;
  const name = typeof item.name === 'string' ? item.name.trim() : '';
  const dmsFileRef = typeof item.dmsFileRef === 'string' ? item.dmsFileRef.trim() : '';
  if (!name || !dmsFileRef.startsWith('dms:FILE:')) return null;
  return {
    id: typeof item.id === 'string' && item.id ? item.id : createAssistantId('file'),
    name,
    type: typeof item.type === 'string' && item.type ? item.type : 'application/octet-stream',
    size: typeof item.size === 'number' && Number.isFinite(item.size) ? item.size : 0,
    dmsFileRef,
    supportTarget: item.supportTarget === 'QUBEPRO_SAAS' || item.supportTarget === 'TENANT_INTERNAL'
      ? item.supportTarget
      : undefined,
  };
}

export function normalizeAssistantSuggestedActions(value: unknown, fallbackLabel = 'İşlemi başlat'): AssistantSuggestedAction[] {
  if (!Array.isArray(value)) return [];
  const allowed = new Set<AssistantActionType>([
    'open_support_ticket',
    'capture_screenshot',
    'focus_support_description',
    'support_main_menu',
    'create_report_draft',
    'create_automation_draft',
    'prepare_product_form',
    'open_help_doc',
    'none',
  ]);

  return value
    .map<AssistantSuggestedAction | null>((item) => {
      if (!item || typeof item !== 'object') return null;
      const record = item as Record<string, unknown>;
      const rawType = typeof record.type === 'string' ? record.type : 'none';
      const type = allowed.has(rawType as AssistantActionType) ? rawType as AssistantActionType : 'none';
      if (!type || type === 'none') return null;
      return {
        type,
        label: typeof record.label === 'string' && record.label.trim() ? record.label.trim() : fallbackLabel,
        requiresConfirmation: record.requiresConfirmation === true,
        payload: record.payload && typeof record.payload === 'object' ? record.payload as Record<string, unknown> : undefined,
      };
    })
    .filter((item): item is AssistantSuggestedAction => Boolean(item));
}

export function resolveAssistantLanguageCode(language?: string): string {
  return String(language || 'tr').toLowerCase().split('-')[0] || 'tr';
}

export function getAssistantComposerTexts(language?: string): AssistantComposerTexts {
  const code = resolveAssistantLanguageCode(language);
  if (code === 'en') {
    return {
      menuLabel: 'Tools and attachments',
      supportTitle: 'Open support ticket',
      supportDescription: 'Open a form for an issue, error, or request',
      screenTitle: 'Attach screenshot',
      screenDescription: 'Attach the current screen to the conversation',
      fileTitle: 'Attach file',
      fileDescription: 'Attach a document or image',
      helpTitle: 'Explain this screen',
      helpDescription: 'Summarize what the current screen does',
      reportTitle: 'Prepare report draft',
      reportDescription: 'Turn the request into a report draft',
      automationTitle: 'Prepare automation draft',
      automationDescription: 'Turn repetitive work into an automation idea',
      sendTitle: 'Send',
      stopTitle: 'Stop response',
    };
  }

  return {
    menuLabel: 'Yetenekler ve ekler',
    supportTitle: 'Destek kaydı aç',
    supportDescription: 'Sorun, hata veya talep için form aç',
    screenTitle: 'Ekran görüntüsü ekle',
    screenDescription: 'Aktif ekranı konuşmaya ekle',
    fileTitle: 'Dosya ekle',
    fileDescription: 'Doküman veya görsel ekle',
    helpTitle: 'Bu ekranı açıkla',
    helpDescription: 'Mevcut sayfanın ne işe yaradığını özetle',
    reportTitle: 'Rapor taslağı hazırla',
    reportDescription: 'İsteği rapor taslağına dönüştür',
    automationTitle: 'Otomasyon taslağı hazırla',
    automationDescription: 'Tekrarlı işi otomasyon fikrine çevir',
    sendTitle: 'Gönder',
    stopTitle: 'Yanıtı durdur',
  };
}

export function getAssistantComposerActionDescriptors(language?: string): AssistantComposerActionDescriptor[] {
  const texts = getAssistantComposerTexts(language);
  const descriptorById: Record<AssistantComposerActionId, AssistantComposerActionDescriptor> = {
    'capture-screen': {
      id: 'capture-screen',
      title: texts.screenTitle,
      description: texts.screenDescription,
      iconName: 'camera',
    },
    'attach-file': {
      id: 'attach-file',
      title: texts.fileTitle,
      description: texts.fileDescription,
      iconName: 'paperclip',
    },
    'support-ticket': {
      id: 'support-ticket',
      title: texts.supportTitle,
      description: texts.supportDescription,
      iconName: 'support',
    },
    'explain-screen': {
      id: 'explain-screen',
      title: texts.helpTitle,
      description: texts.helpDescription,
      iconName: 'help',
    },
    'report-draft': {
      id: 'report-draft',
      title: texts.reportTitle,
      description: texts.reportDescription,
      iconName: 'chart',
    },
    'automation-draft': {
      id: 'automation-draft',
      title: texts.automationTitle,
      description: texts.automationDescription,
      iconName: 'sparkles',
    },
  };

  return ASSISTANT_COMPOSER_ACTION_ORDER.map((id) => descriptorById[id]);
}
